(function () {
  'use strict';

  /* Drag-to-scroll (the shared helper in eb-drag-scroll.js) plus an `onSettle`
     scroll listener for arrow/fade updates. */
  function initDragScroll(el, onSettle) {
    window.EbDragScroll.attach(el);
    if (onSettle) {
      el.addEventListener('scroll', onSettle);
    }
  }

  var REDUCE =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* One product slider inside a tab panel: drag + arrow paging with an
     end-state. `update()` recomputes arrow visibility — call it when the panel
     becomes visible (a hidden panel measures 0 width). */
  function Scroller(root) {
    this.track = root.querySelector('[data-ctabs-track]');
    this.prevBtn = root.querySelector('[data-ctabs-prev]');
    this.nextBtn = root.querySelector('[data-ctabs-next]');
    if (!this.track) return;

    var self = this;
    initDragScroll(this.track, function () {
      self.update();
    });

    function page(dir) {
      self.track.scrollBy({
        left: dir * self.track.clientWidth * 0.85,
        behavior: REDUCE ? 'auto' : 'smooth'
      });
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        page(-1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        page(1);
      });
    }
    this.update();
  }

  Scroller.prototype.update = function () {
    if (!this.track || (!this.prevBtn && !this.nextBtn)) return;
    var t = this.track;
    // 1px slack absorbs sub-pixel rounding at the ends.
    var maxScroll = t.scrollWidth - t.clientWidth - 1;
    var scrollable = maxScroll > 1;
    if (this.prevBtn) this.prevBtn.hidden = !scrollable || t.scrollLeft <= 0;
    if (this.nextBtn) this.nextBtn.hidden = !scrollable || t.scrollLeft >= maxScroll;
  };

  /* The tabbed section: switches panels, re-points "See all", and keeps the tab
     bar drag-scrollable with edge fades when the tabs overflow. */
  class EbCollectionTabs extends HTMLElement {
    connectedCallback() {
      this.tabsBar = this.querySelector('[data-ctabs-tabs]');
      this.tabsWrap = this.querySelector('.eb-ctabs__tabs-wrap');
      this.tabs = Array.prototype.slice.call(
        this.querySelectorAll('[data-ctabs-tab]')
      );
      this.panels = Array.prototype.slice.call(
        this.querySelectorAll('[data-ctabs-panel]')
      );
      this.seeAllWrap = this.querySelector('.eb-ctabs__see-all-wrap');
      this.seeAll = this.querySelector('.eb-ctabs__see-all');

      // One slider controller per panel, keyed by the panel's data-index.
      this.scrollers = {};
      var self = this;
      this.panels.forEach(function (panel) {
        var scroller = panel.querySelector('[data-ctabs-scroller]');
        if (scroller) self.scrollers[panel.dataset.index] = new Scroller(scroller);
      });

      this.initTabs();
      this.initTabBarScroll();

      // Sync arrows/fades/See-all to whichever tab rendered active.
      var active = this.tabs.find(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      });
      this.activate(active ? active.dataset.index : '0', false);
    }

    initTabs() {
      if (!this.tabs.length) return;
      var self = this;

      this.tabsBar.addEventListener('click', function (e) {
        var tab = e.target.closest('[data-ctabs-tab]');
        if (tab) self.activate(tab.dataset.index, true);
      });

      // Roving-tabindex keyboard nav across the tablist.
      this.tabsBar.addEventListener('keydown', function (e) {
        var i = self.tabs.indexOf(document.activeElement);
        if (i === -1) return;
        var next = -1;
        if (e.key === 'ArrowRight') next = (i + 1) % self.tabs.length;
        else if (e.key === 'ArrowLeft') next = (i - 1 + self.tabs.length) % self.tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = self.tabs.length - 1;
        if (next === -1) return;
        e.preventDefault();
        self.activate(self.tabs[next].dataset.index, true);
        self.tabs[next].focus();
      });
    }

    activate(index, moveFocus) {
      var self = this;
      var activeSeeAll = '';
      var activeHasMore = false;

      this.tabs.forEach(function (tab) {
        var on = tab.dataset.index === index;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
        if (on) {
          activeSeeAll = tab.dataset.seeAll || '';
          activeHasMore = tab.dataset.hasMore === 'true';
          // Reveal a partially off-screen tab when it's selected.
          if (moveFocus && tab.scrollIntoView) {
            tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        }
      });

      this.panels.forEach(function (panel) {
        panel.hidden = panel.dataset.index !== index;
      });

      // The visible panel was display:none, so its slider measured 0 — recompute.
      var scroller = this.scrollers[index];
      if (scroller) scroller.update();

      // Show "See all" only when this tab links somewhere AND has more products
      // than the section already displays.
      if (this.seeAll && activeSeeAll) this.seeAll.href = activeSeeAll;
      if (this.seeAllWrap) {
        this.seeAllWrap.hidden = !(activeHasMore && activeSeeAll);
      }

      // Debounce isn't needed; overflow state is cheap to read.
      self.updateOverflow();
    }

    initTabBarScroll() {
      if (!this.tabsBar) return;
      var self = this;
      initDragScroll(this.tabsBar, function () {
        self.updateOverflow();
      });
      window.addEventListener('resize', function () {
        self.updateOverflow();
        // Keep the visible panel's arrows correct across breakpoints.
        Object.keys(self.scrollers).forEach(function (k) {
          if (!self.panels.some(function (p) { return p.dataset.index === k && p.hidden; })) {
            self.scrollers[k].update();
          }
        });
      });
      this.updateOverflow();
    }

    updateOverflow() {
      if (!this.tabsWrap || !this.tabsBar) return;
      var bar = this.tabsBar;
      var maxScroll = bar.scrollWidth - bar.clientWidth - 1;
      var overflow = maxScroll > 1;
      this.tabsWrap.classList.toggle('is-overflow-start', overflow && bar.scrollLeft > 0);
      this.tabsWrap.classList.toggle('is-overflow-end', overflow && bar.scrollLeft < maxScroll);
    }
  }

  if (!customElements.get('eb-collection-tabs')) {
    customElements.define('eb-collection-tabs', EbCollectionTabs);
  }
})();
